import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import PartnerMediaManager from "./PartnerMediaManager";

/**
 * Minimal partner course editor. Supports metadata + modules/lessons/quiz JSON.
 * A richer WYSIWYG builder is a Phase 2 nice-to-have; this is a functional
 * Phase 1 editor that reuses the existing Course schema.
 */
export default function PartnerCourseEdit() {
  const { id } = useParams();
  const [course, setCourse] = useState(null);
  const [busy, setBusy] = useState(false);
  const [modulesJson, setModulesJson] = useState("");
  const [previewScreenshots, setPreviewScreenshots] = useState("");

  const load = () =>
    api.get(`/api/partner/courses/${id}`).then(({ data }) => {
      setCourse(data);
      setModulesJson(JSON.stringify(data.modules || [], null, 2));
      setPreviewScreenshots((data.preview_screenshots || []).join("\n"));
    });

  useEffect(() => { load(); }, [id]);

  if (!course) return <div className="p-8">Loading course…</div>;

  const set = (k, v) => setCourse((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      let modules;
      try { modules = JSON.parse(modulesJson || "[]"); }
      catch { toast.error("Modules JSON is invalid"); setBusy(false); return; }
      const payload = {
        title: course.title,
        short_description: course.short_description,
        description: course.description,
        price_usd: Number(course.price_usd),
        level: course.level,
        language: course.language,
        duration_hours: Number(course.duration_hours),
        thumbnail_url: course.thumbnail_url || "",
        certificate_title: course.certificate_title || "Certificate of Completion",
        modules,
        preview_screenshots: previewScreenshots.split("\n").map((s) => s.trim()).filter(Boolean),
        preview_teaser_video: course.preview_teaser_video || "",
        demo_video_url: course.demo_video_url || "",
      };
      const { data } = await api.patch(`/api/partner/courses/${id}`, payload);
      setCourse(data);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const publish = async () => {
    try {
      await api.post(`/api/partner/courses/${id}/publish`);
      await load();
      toast.success("Published");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish failed");
    }
  };

  return (
    <div className="max-w-4xl space-y-6" data-testid="partner-course-edit-root">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/partner/courses" className="text-sm text-gray-600 hover:underline"
            data-testid="partner-course-back-link">← Back to courses</Link>
          <h1 className="text-2xl font-bold mt-1">{course.title}</h1>
          <div className="flex items-center gap-2 mt-1">
            <Badge variant={course.is_published ? "default" : "secondary"} className="text-xs">
              {course.is_published ? "Published" : "Draft"}
            </Badge>
            <Badge className="text-xs bg-gray-100 text-gray-700">
              Compliance: {course.compliance_status || "clean"}
            </Badge>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={busy}
            data-testid="partner-course-save-btn">
            {busy ? "Saving…" : "Save"}
          </Button>
          {!course.is_published && (
            <Button onClick={publish}
              data-testid="partner-course-publish-btn"
              className="bg-[#DC2626] hover:bg-[#B91C1C]">
              Publish
            </Button>
          )}
        </div>
      </div>

      {course.compliance_flags?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-md p-4"
          data-testid="partner-compliance-alert">
          <div className="font-semibold text-amber-800 mb-1">Compliance review pending</div>
          <div className="text-sm text-amber-700">
            Flagged keywords: {course.compliance_flags.join(", ")}. An admin must approve before publishing.
          </div>
        </div>
      )}

      <Card>
        <CardHeader><CardTitle>Basics</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={course.title} onChange={(e) => set("title", e.target.value)}
              data-testid="edit-title-input" />
          </div>
          <div>
            <Label>Short description</Label>
            <Input value={course.short_description || ""}
              data-testid="edit-short-input"
              onChange={(e) => set("short_description", e.target.value)} />
          </div>
          <div>
            <Label>Full description</Label>
            <Textarea rows={4} value={course.description || ""}
              data-testid="edit-desc-input"
              onChange={(e) => set("description", e.target.value)} />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Price (USD)</Label>
              <Input type="number" value={course.price_usd}
                data-testid="edit-price-input"
                onChange={(e) => set("price_usd", e.target.value)} />
            </div>
            <div>
              <Label>Level</Label>
              <select className="w-full h-10 px-3 rounded-md border"
                data-testid="edit-level-input"
                value={course.level} onChange={(e) => set("level", e.target.value)}>
                <option>Beginner</option><option>Intermediate</option><option>Advanced</option>
              </select>
            </div>
            <div>
              <Label>Duration (hrs)</Label>
              <Input type="number" step="0.5" value={course.duration_hours}
                data-testid="edit-duration-input"
                onChange={(e) => set("duration_hours", e.target.value)} />
            </div>
          </div>
          <div>
            <Label>Thumbnail URL</Label>
            <Input value={course.thumbnail_url || ""}
              data-testid="edit-thumb-input"
              onChange={(e) => set("thumbnail_url", e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Course previews</CardTitle>
          <p className="text-xs text-gray-500">Screenshots, teaser video, demo video URL, free lesson.</p>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Preview screenshots (one URL per line, up to 5)</Label>
            <Textarea rows={3} value={previewScreenshots}
              data-testid="edit-screenshots-input"
              onChange={(e) => setPreviewScreenshots(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Teaser video URL (60-90s)</Label>
              <Input value={course.preview_teaser_video || ""}
                data-testid="edit-teaser-input"
                onChange={(e) => set("preview_teaser_video", e.target.value)} />
            </div>
            <div>
              <Label>Demo/walkthrough video URL</Label>
              <Input value={course.demo_video_url || ""}
                data-testid="edit-demo-video-input"
                onChange={(e) => set("demo_video_url", e.target.value)} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Course media (selective upload)</CardTitle>
          <p className="text-xs text-gray-500">
            Images (≤ 15 MB), PDFs (≤ 30 MB), or videos (≤ 500 MB). Counts against your partner quota.
            Uploaded assets are served publicly once the course is published.
          </p>
        </CardHeader>
        <CardContent>
          <PartnerMediaManager courseId={course.id} />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Modules (raw JSON)</CardTitle>
          <p className="text-xs text-gray-500">
            Advanced editor coming — for now edit the modules array directly.
            Each module: {`{id, title, lessons: [{id, title, body, video_url?, is_free_preview?}], quiz?}`}
          </p>
        </CardHeader>
        <CardContent>
          <Textarea rows={16} value={modulesJson}
            data-testid="edit-modules-json"
            onChange={(e) => setModulesJson(e.target.value)}
            className="font-mono text-xs" />
        </CardContent>
      </Card>
    </div>
  );
}
